// After a return (F444, Gianluca 2026-10-28): once the companion says the
// return is done (the bar gone, the entry committed), the window goes to
// the logbook page of the session's last flight with a note that
// everything was sent; a return held or refused goes to Returns waiting.
// Rendered under the host alone, above the page, inside the router.

import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { queries } from "@/api";
import { HandoverCall } from "@/components/Handover";
import { useHostEvents } from "@/host/events";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import type { ReturnOutcome, SessionMessage, StatusRow } from "@/host/types";

type Note =
  | { kind: "done"; reg: string; outcome: ReturnOutcome; airframeId: string | null; flightId: string | null; trouble: string | null }
  | { kind: "held"; reg: string; error: string };

/** The reason a return waits, in the words of the line; the companion's own sentence stays below it. */
export function heldReason(error: string): string {
  if (/no longer yours/i.test(error)) return "The aircraft was no longer yours when the return came: the state is kept for the owner or a captain to accept.";
  return "The platform could not take the return now: it is kept on this PC and the next return sends it.";
}

export function AfterReturn() {
  const host = useHost();
  const events = useHostEvents();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [note, setNote] = useState<Note | null>(null);
  // The number of the last message handled: each is read once, whatever
  // the log above keeps (it is capped, the number is not).
  const seen = useRef(0);
  useEffect(() => {
    if (!host || !events.latest || events.latest.seq <= seen.current) return;
    seen.current = events.latest.seq;
    void follow(events.latest.message);
    async function follow(m: SessionMessage) {
      if (m.kind === "started" || m.kind === "watch_started") {
        setNote(null);
        return;
      }
      if (m.kind !== "done" && m.kind !== "failed") return;
      if (m.kind === "done" && !m.outcome) return;
      let rows: StatusRow[] = [];
      try {
        rows = await queryClient.fetchQuery({ ...hostQueries.status(host!), staleTime: 0 });
      } catch {
        // The rows unread: the note still says what the companion said.
      }
      const row = rows.find((r) => r.uuid === m.hs_uuid);
      const reg = row?.reg ?? (m.kind === "done" ? regOf(m.summary) : null) ?? "";
      if (m.kind === "failed") {
        if (!m.held) return;
        setNote({ kind: "held", reg, error: m.error });
        queryClient.invalidateQueries({ queryKey: ["host"] });
        navigate({ to: "/pc/returns" });
        return;
      }
      const outcome = m.outcome!;
      const id = row?.local?.platform_id ?? row?.platform?.id ?? null;
      let fid: string | null = null;
      let trouble: string | null = null;
      if (id && outcome.flights_added > 0) {
        try {
          await queryClient.invalidateQueries({ queryKey: ["flights", id] });
          const list = await queryClient.fetchQuery({ ...queries.flights(id), staleTime: 0 });
          fid = list.items[0]?.id ?? null;
        } catch (e) {
          console.warn("the logbook could not be read after the return", e);
          trouble = "The logbook could not be read just now; open it when the platform answers.";
        }
      }
      setNote({ kind: "done", reg, outcome, airframeId: id, flightId: fid, trouble });
      if (id && fid) navigate({ to: "/airframes/$id/flights/$fid", params: { id, fid } });
      else if (id && !trouble) navigate({ to: "/airframes/$id/ledger", params: { id } });
    }
  }, [host, events.latest, queryClient, navigate]);
  if (!host || !note) return null;
  if (note.kind === "held") {
    return (
      <div className="alert mb-3" role="status" data-testid="return-held">
        <p className="font-semibold">{note.reg ? `The return of ${note.reg} is waiting` : "The return is waiting"}</p>
        <p className="text-sm">{heldReason(note.error)}</p>
        <p className="muted text-xs">{note.error}</p>
        <p className="mt-2 flex items-center gap-2">
          <Link to="/pc/returns" className="btn btn-sm">
            Returns waiting
          </Link>
          <button type="button" className="btn btn-sm" onClick={() => setNote(null)}>
            Dismiss
          </button>
        </p>
      </div>
    );
  }
  const o = note.outcome;
  const flights = o.flights_added === 0 ? "no new flight" : `${o.flights_added} new flight${o.flights_added === 1 ? "" : "s"} (${o.flights_attributed} yours)`;
  const recordings = o.recordings_read === 0 ? "" : `, ${o.recordings_read} recording${o.recordings_read === 1 ? "" : "s"} of the aircraft read`;
  const files = o.files === 0 ? ", the numbers only, no files" : `, ${o.files} files sent`;
  return (
    <div className="card mb-3 border-ok" role="status" data-testid="return-done">
      <p className="font-semibold">{note.reg ? `${note.reg} returned and everything was sent` : "The aircraft is returned and everything was sent"}</p>
      <p className="text-sm">
        Tech log entry {o.entry}, {flights}
        {recordings}
        {files}; the aircraft is {o.still_yours ? "still yours" : "available again"}.{" "}
        {note.flightId ? "This is the flight of the session, with its details." : o.flights_added === 0 ? "No flight was added by this session: the logbook is as it was." : (note.trouble ?? "")}
      </p>
      <p className="mt-2 flex items-center gap-2">
        {note.airframeId ? (
          <Link to="/airframes/$id/ledger" params={{ id: note.airframeId }} className="btn btn-sm">
            The logbook
          </Link>
        ) : null}
        <button type="button" className="btn btn-sm" onClick={() => setNote(null)} data-testid="return-dismiss">
          Dismiss
        </button>
      </p>
      {note.airframeId ? (
        <div className="mt-3">
          <HandoverCall airframeId={note.airframeId} reg={note.reg} />
        </div>
      ) : null}
    </div>
  );
}

/** The registration the companion's summary opens with ("Returned N75TJ: ..."), when this PC's rows do not know the airframe. */
function regOf(summary: string): string | null {
  return /^Returned ([A-Z0-9-]+)/.exec(summary)?.[1] ?? null;
}
